import { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { TItemSelector } from 'libs/design-system/src/types/item_selector/item-selector';
import { ItemSelectorGroup, Modal } from '@unified-checkout/design-system';

import { useNotificationStore } from '@app/dashboard/utils/notifier';
import type {
  IReportTemplateProps,
  TReportTemplateTypes,
} from '@app/dashboard/types/shared/modals/reports-template';
import { REPORT_TEMPLATE_TYPES } from '@app/dashboard/constants/shared/modals/reports-template';
import { NOTIFICATION_STATUS } from '@app/dashboard/constants/notifier';

import styles from './reports-template.module.scss';

const ReportsTemplate = ({
  isOpen,
  isLoading = false,
  handleClose,
  handleSubmit,
}: IReportTemplateProps) => {
  const [selectedTemplate, setSelectedTemplate] =
    useState<TReportTemplateTypes>(
      REPORT_TEMPLATE_TYPES.UPDATED as TReportTemplateTypes
    );
  const { t } = useTranslation();

  const { show: isNotifyShown, state: notifyState } = useNotificationStore();

  const disableWhenNotifier =
    isNotifyShown && notifyState === NOTIFICATION_STATUS.SUCCESS;

  const handleSelectTemplate = useCallback((id: string) => {
    setSelectedTemplate(id as TReportTemplateTypes);
  }, []);

  const handleGenerate = useCallback(() => {
    if (!selectedTemplate) return;
    handleSubmit?.(selectedTemplate);
  }, [selectedTemplate, handleSubmit]);

  const items: TItemSelector[] = useMemo(
    () => [
      {
        id: REPORT_TEMPLATE_TYPES.UPDATED,
        text: t('innerLayout.shared.reportsTemplate.newReportTemplate'),
        hintText: t('innerLayout.shared.reportsTemplate.newReportTemplateDesc'),
        size: 'md',
        selected: selectedTemplate === REPORT_TEMPLATE_TYPES.UPDATED,
        badgeProps: {
          color: 'primary',
          size: 'sm',
          text: t('innerLayout.shared.reportsTemplate.updated'),
        },
        onClickHandler: () =>
          handleSelectTemplate(REPORT_TEMPLATE_TYPES.UPDATED),
      },
      {
        id: REPORT_TEMPLATE_TYPES.CLASSIC,
        text: t('innerLayout.shared.reportsTemplate.oldReportTemplate'),
        hintText: t('innerLayout.shared.reportsTemplate.oldReportTemplateDesc'),
        badgeProps: {
          color: 'gray',
          size: 'sm',
          text: t('innerLayout.shared.reportsTemplate.classic'),
        },
        size: 'md',
        selected: selectedTemplate === REPORT_TEMPLATE_TYPES.CLASSIC,
        onClickHandler: () =>
          handleSelectTemplate(REPORT_TEMPLATE_TYPES.CLASSIC),
      },
    ],
    [t, selectedTemplate]
  );

  useEffect(() => {
    return () => {
      setSelectedTemplate(
        REPORT_TEMPLATE_TYPES.UPDATED as TReportTemplateTypes
      );
    };
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      hasCloseIcon={!(isLoading || disableWhenNotifier)}
      closeOnClickOutside={!(isLoading || disableWhenNotifier)}
      isOverlayAboveModal
      onCloseHandler={handleClose}
      title={t('innerLayout.shared.reportsTemplate.title')}
      supportingText={t('innerLayout.shared.reportsTemplate.description')}
      wrapperClassName={styles['reports-template__modal']}
      btnsProps={[
        {
          text: t('innerLayout.shared.reportsTemplate.generateNow'),
          variant: 'contained',
          color: 'primary',
          onClickHandler: handleGenerate,
          disabled: isLoading || !selectedTemplate || disableWhenNotifier,
          loading: isLoading,
        },
        {
          text: t('form.cancel'),
          variant: 'outlined',
          color: 'secondary',
          onClickHandler: handleClose,
          disabled: isLoading || disableWhenNotifier,
        },
      ]}
    >
      <div
        className={styles['reports-template__modal__content']}
        data-reports-generating={isLoading}
      >
        <ItemSelectorGroup
          label={t('innerLayout.shared.reportsTemplate.chooseTemplate')}
          key="item-selector-group"
          type="radio"
          orientation="horizontal"
          items={items}
        />
      </div>
    </Modal>
  );
};

export default ReportsTemplate;
